import React from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import keycloak, { buildKeycloakLoginOptions, initKeycloak } from "../keycloak";

/**
 * Redirects unauthenticated visitors to Keycloak.
 * Only mounted on protected routes (`/dds`, `/workflow`) — never on `/` or `/welcome` (US-806).
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = React.useState<"pending" | "ok" | "redirecting">("pending");

  React.useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await initKeycloak();
      } catch {
        // Fall through to login — interactive login can still recover.
      }
      if (cancelled) return;
      if (keycloak.authenticated) {
        setPhase("ok");
        return;
      }
      setPhase("redirecting");
      void keycloak.login(buildKeycloakLoginOptions(window.location.href, i18n.language));
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  if (phase !== "ok") {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}
        data-testid="auth-redirect"
      >
        <Spin size="large" tip={t("auth.authorizing")} />
      </div>
    );
  }

  return <>{children}</>;
}
